import { addDays } from '@sivan/shared';
import { useId, useState } from 'react';
import { Icon } from '../ui/Icon';
import { formatDate, today } from './handover-draft';

/** True when an actual handover date is after today in Israel (allowed, but usually a mistake). */
export const isFuture = (iso: string, now = new Date()): boolean => iso !== '' && iso > today(now);

/** "היום", "אתמול" or nothing, for an actual handover date. */
export function relativeDay(iso: string, now = new Date()): string {
  const day = today(now);
  if (iso === day) return 'היום';
  if (iso === addDays(day, -1)) return 'אתמול';
  return '';
}

export function FutureDateWarning({ id }: { readonly id?: string | undefined }) {
  return (
    <p className="date-warning" id={id} role="status">
      <Icon name="alert" />
      <span>התאריך בעתיד. תאריך מסירה בפועל הוא בדרך כלל היום או יום שעבר.</span>
    </p>
  );
}

/**
 * Choosing the actual handover date on a phone: today, yesterday or another date (the native
 * picker), with the appointment shown as context. The date is the day the apartment was actually
 * handed over; the appointment in the schedule is a separate value and does not change.
 */
export function DateSheet({
  value,
  appointment,
  onSubmit,
  onCancel,
}: {
  readonly value: string;
  /** The appointment in the schedule, as text ("15/10/2026 09:00"), or null. */
  readonly appointment: string | null;
  readonly onSubmit: (iso: string) => void;
  readonly onCancel: () => void;
}) {
  const id = useId();
  const day = today();
  const yesterday = addDays(day, -1);
  const [choice, setChoice] = useState<'today' | 'yesterday' | 'other'>(
    value === day ? 'today' : value === yesterday ? 'yesterday' : value === '' ? 'today' : 'other',
  );
  const [other, setOther] = useState(value === '' ? day : value);
  const [error, setError] = useState(false);
  const chosen = choice === 'today' ? day : choice === 'yesterday' ? yesterday : other;
  return (
    <form
      className="date-sheet"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(chosen)) {
          setError(true);
          return;
        }
        onSubmit(chosen);
      }}
    >
      <p className="hint">
        היום שבו הדירה נמסרה בפועל.{' '}
        {appointment === null
          ? 'למסירה אין מועד ביומן.'
          : `המועד ביומן (${appointment}) נשאר כפי שהוא.`}
      </p>
      <fieldset className="date-sheet__choices">
        <legend className="visually-hidden">תאריך מסירה בפועל</legend>
        {(
          [
            ['today', 'היום', day],
            ['yesterday', 'אתמול', yesterday],
            ['other', 'תאריך אחר', null],
          ] as const
        ).map(([key, label, date]) => (
          <label key={key} className="date-sheet__choice">
            <input
              type="radio"
              name={`${id}-day`}
              checked={choice === key}
              onChange={() => {
                setChoice(key);
                setError(false);
              }}
            />
            <span>
              <b>{label}</b>
              {date !== null && <span className="date-sheet__date"> · {formatDate(date)}</span>}
            </span>
          </label>
        ))}
      </fieldset>
      {choice === 'other' && (
        <label className="field">
          <span>תאריך מסירה בפועל</span>
          <input
            type="date"
            value={other}
            aria-invalid={error}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(e) => {
              setOther(e.target.value);
              setError(false);
            }}
          />
          {error && (
            <p className="field__error" id={`${id}-error`}>
              יש לבחור תאריך
            </p>
          )}
        </label>
      )}
      {isFuture(chosen) && <FutureDateWarning />}
      <div className="form-actions">
        <button type="submit" className="button button--primary">
          בחירת התאריך
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}
